import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import AuthService, { privileges } from '@/shared/services/AuthService';
import DataCacheService from '@/shared/services/DataCacheService';
import ToastrService from '@/shared/services/ToastrService';
import Checkbox from '@/shared/components/input/Checkbox';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import { useGetSsoSettings } from '@/shared/hooks/settings/useGetSsoSettings';
import { useUpdateTwoFaRequired } from '@/shared/hooks/settings/useUpdateTwoFaRequired';
import SettingsSection from './SettingsSection';
import SettingsField from './SettingsField';
import SsoSettingsModal from '../modals/SsoSettingsModal';
const messages = defineMessages({
    SecuritySectionTitle: {
        id: 'SecuritySectionTitle',
        defaultMessage: 'Security',
    },
    SsoSignInMethod: {
        id: 'SsoSignInMethod',
        defaultMessage: 'Sign-in method',
    },
    SsoProviderNone: {
        id: 'SsoProviderNone',
        defaultMessage: 'E-mail address and password',
    },
    SsoProviderEntra: {
        id: 'SsoProviderEntra',
        defaultMessage: 'Microsoft Entra ID',
    },
    TwoFaRequired: {
        id: 'TwoFaRequired',
        defaultMessage: 'All users should enable 2-factor authentication',
    },
    TwoFaTooltip: {
        id: 'TwoFaTooltip',
        defaultMessage: 'If this option is enabled, users that do not have 2FA enabled will be asked to enable 2FA on the next login.',
    },
    SsoConfigure: {
        id: 'SsoConfigure',
        defaultMessage: 'Configure',
    },
});
const providerLabel = (intl, provider) => {
    switch (provider) {
        case 1:
            return intl.formatMessage(messages.SsoProviderEntra);
        case 2:
            return 'Google';
        case 3:
            return 'PwC';
        case 4:
            return 'Visma Connect';
        default:
            return intl.formatMessage(messages.SsoProviderNone);
    }
};
const SecuritySection = () => {
    var _a, _b, _c;
    const intl = useIntl();
    const [showModal, setShowModal] = useState(false);
    const [twoFaRequired, setTwoFaRequired] = useState(() => { var _a; return !!((_a = DataCacheService.getDataCache()) === null || _a === void 0 ? void 0 : _a.twoFaRequired); });
    const canRead = AuthService.hasPermission({
        functionality: 'Settings',
        privilege: privileges.Read,
    });
    const canUpdate = AuthService.hasPermission({
        functionality: 'Settings',
        privilege: privileges.Update,
    });
    // No fetch on page load: the summary comes from the app data cache. The disabled query only
    // subscribes to the cache entry the modal populates, so the summary refreshes after a save.
    const { data: ssoSettings } = useGetSsoSettings(false);
    const { updateTwoFaRequired } = useUpdateTwoFaRequired();
    if (!canRead)
        return null;
    const provider = (_c = (_a = ssoSettings === null || ssoSettings === void 0 ? void 0 : ssoSettings.ssoProvider) !== null && _a !== void 0 ? _a : (_b = DataCacheService.getDataCache()) === null || _b === void 0 ? void 0 : _b.ssoProvider) !== null && _c !== void 0 ? _c : 0;
    const handleTwoFaToggle = async (value) => {
        try {
            await updateTwoFaRequired(value);
            setTwoFaRequired(value);
            ToastrService.changesSaved(intl);
        }
        catch (_a) {
            // error toastr already shown by useUpdateTwoFaRequired hook
        }
    };
    return (_jsxs(SettingsSection, Object.assign({ title: intl.formatMessage(messages.SecuritySectionTitle), securityKey: "Settings" }, { children: [_jsxs(SettingsField, Object.assign({ label: _jsxs(_Fragment, { children: [intl.formatMessage(messages.SsoSignInMethod), _jsx(ZendeskHelp, { article: "13095511761820" })] }) }, { children: [_jsx("span", Object.assign({ className: "margin10R" }, { children: providerLabel(intl, provider) })), _jsxs("button", Object.assign({ type: "button", className: "btn btn-default btn-sm", disabled: !canUpdate, onClick: () => setShowModal(true) }, { children: [_jsx("i", { className: "fa fa-cog margin5R" }), _jsx("span", { children: intl.formatMessage(messages.SsoConfigure) })] }))] })), _jsx(SettingsField, Object.assign({ label: intl.formatMessage(messages.TwoFaRequired), tooltip: intl.formatMessage(messages.TwoFaTooltip) }, { children: canUpdate ? (_jsx(Checkbox, { value: twoFaRequired, onClick: handleTwoFaToggle })) : (_jsx("i", { className: `fa fa-fw ${twoFaRequired ? 'fa-check-square-o' : 'fa-square-o'}` })) })), showModal && _jsx(SsoSettingsModal, { show: showModal, onClose: () => setShowModal(false) })] })));
};
export default SecuritySection;
